import {FC, useEffect, useMemo, useState} from "react";
import {ModalWithContent} from "../../../../../../common/ModalWithContent/ModalWithContent";
import {DateField} from "../../../../../../common/InputComponents/DateField/DateField";
import {useFormik} from "formik";
import classes from "./AddEditMedicationModal.module.scss";
import {TimeField} from "../../../../../../common/InputComponents/TimeField/TimeField";
import {SelectField} from "../../../../../../common/InputComponents/SelectField/SelectField";
import {TextInput} from "../../../../../../common/InputComponents/TextInput/TextInput";
import {useDispatch, useSelector} from "react-redux";
import {getDefaultMedications} from "../../../../../../../redux/selectors/patientsSelector";
import {Button, Chip} from "@mui/material";
import {IMedication, medicationDosagesUnits, MedicationDosageUnit, ToastType} from "../../../../../../../utils/interfaces";
import {getMedicationForCreation, getMedicationForEdit} from "../../../../../../../utils/dataHandlers";
import {ADD_MEDICATION_REQUEST, EDIT_MEDICATION_REQUEST, SET_TOAST} from "../../../../../../../redux/actions";
import {useLocation} from "react-router-dom";
import {SelectedMedicationData} from "../../Medications";
import {isAfter, isBefore, isToday, parse} from "date-fns";
import {validationSchemaMedications} from "../../../../../../../utils/inputUtils";

type AddEditMedicationModalProps = {
  type: SelectedMedicationData['type'];
  onClose: () => void;
  medication: IMedication | null;
  getPatientMedications: () => void;
}

const formatMedicationForForm = (medication: IMedication) => ({
  name: medication.name,
  defaultName: medication.name,
  note: medication.note,
  dosageUnit: medication.dosageUnit,
  dosageValue: medication.dosageValue,
  times: medication.times,
  startDate: parse(medication.startDate, 'yyyy-MM-dd', new Date()),
  endDate: parse(medication.endDate, 'yyyy-MM-dd', new Date()),
})

const initialMedicationValues = {
  name: '',
  defaultName: '',
  note: '',
  dosageUnit: 'mg',
  dosageValue: '',
  times: [],
  startDate: undefined,
  endDate: undefined,
};

const unitOptions = medicationDosagesUnits.map(unit => ({
  id: unit,
  label: unit,
  name: unit,
}))

type DefaultMedication = {
  description: string;
  dosageUnit: MedicationDosageUnit;
  dosageValue: number;
  name: string;
  type: string;
}


export const AddEditMedicationModal: FC<AddEditMedicationModalProps> = ({type, onClose, medication, getPatientMedications}) => {
  const isEdit = type === 'edit' && !!medication;

  const initialValues = isEdit ? formatMedicationForForm(medication) : initialMedicationValues;
  const dispatch = useDispatch();
  const defaultMedications: DefaultMedication[] = useSelector(getDefaultMedications);

  const defaultMedicationsItems = useMemo(() => {
    if (defaultMedications) {
      const result = defaultMedications?.map(medication => ({
        id: medication.name,
        label: medication.name,
        name: medication.name,
      }));

      result.unshift({id: 'other', label: 'Other', name: 'Other'});

      return result;
    }

    return [];
  }, [defaultMedications]);

  const [times, setTimes] = useState<string[]>(isEdit ? initialMedicationValues.times : []);

  const location = useLocation();
  const id = location.pathname.split('/').pop() || '';

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: validationSchemaMedications,
    onSubmit: values => {
      const selectedDefaultMedication = defaultMedications.find(item => item.name === values.defaultName);

      if (isEdit) {
        const existingMedicationStartDate = parse(medication.startDate, 'yyyy-MM-dd', new Date());
        const existingMedicationEndDate = parse(medication.endDate, 'yyyy-MM-dd', new Date());
        const isMedicationOngoing = isBefore(existingMedicationStartDate, new Date()) && isAfter(existingMedicationEndDate, new Date()) && !isToday(existingMedicationStartDate);

        const updatedMedication = getMedicationForEdit(medication, {
          ...values,
          description: selectedDefaultMedication?.description ?? '',
          type: selectedDefaultMedication?.type ?? 'Other',
          name: selectedDefaultMedication?.name ?? values.name,
        }, times);

        dispatch({
          type: EDIT_MEDICATION_REQUEST,
          payload: {
            updatedMedication,
            patientId: id,
            medicationId: medication.id,
            existingMedication: isMedicationOngoing ? medication : undefined,
          }
        });
      } else {
        const newMedication = getMedicationForCreation({
          ...values,
          type: selectedDefaultMedication?.type ?? 'Other',
          name: selectedDefaultMedication?.name ?? values.name,
        }, times);

        dispatch({
          type: ADD_MEDICATION_REQUEST,
          payload: {
            medication: newMedication,
            patientId: id,
          }
        })
      }

      onCloseModal();
      setTimeout(getPatientMedications, 500)
    }
  });

  useEffect(() => {
    setTimes(type === 'edit' ? medication?.times.map(time => {
      const splittedTime = time.split(':');
      return `${splittedTime[0]}:${splittedTime[1]}`
    }) ?? [] : [])
  }, [type]);

  const timeFormik = useFormik({
    initialValues: {time: ''},
    onSubmit: values => {
      const hasTimeAlreadyBeenAdded = times.find(time => values.time === time);
      if (hasTimeAlreadyBeenAdded) {
        dispatch({
          type: SET_TOAST,
          payload: {
            open: true,
            message: "You have already added this time slot",
            type: ToastType.ERROR,
          }
        })
        return
      }
      if (values.time) {
        setTimes(prev => [...prev, values.time]);
      }
    }
  });

  const defaultSelectedName = formik.values.defaultName;
  const showManualInput = defaultSelectedName === 'other' || isEdit;

  const onDeleteTimeSlot = (indexToDelete: number) => {
    setTimes(prev => prev.filter((_, index) => index !== indexToDelete))
  }

  const onCloseModal = () => {
    formik.resetForm();
    timeFormik.resetForm();
    onClose();
  }

  return (
    <ModalWithContent
      title={`${isEdit ? 'Edit' : 'Add'} medication`}
      open={type === 'add' || type === 'edit'}
      onClose={onCloseModal}
      actions={
        <Button
          disabled={!!Object.values(formik.errors).length || !!Object.values(timeFormik.errors).length || !times.length || ((formik.values.defaultName === 'other' && !formik.values.name.length) || !formik.values.defaultName.length)}
          className={classes.action}
          onClick={formik.submitForm}
          variant="contained">
          Save
        </Button>
      }>
      <div className={classes.container}>
        {isEdit ? null : (
          <SelectField
            formik={formik}
            label="Name"
            id={'defaultName'}
            required
            options={defaultMedicationsItems}
            selectRootClassName={classes.selectRoot}
            selectInputClassName={classes.selectInput}
            labelClassName={classes.label}
            formClassName={classes.formControl}
            helperClassName={classes.helper}
            type="text"
          />
        )}
        {showManualInput ? (
          <TextInput
            formik={formik}
            required
            label="Manual name"
            placeholder="Enter custom name"
            id={'name'}
            type="text"
            inputClassName={classes.input}
            helperClassName={classes.helper}
            labelClassName={classes.label}
            formClassName={classes.formControl}
          />
        ) : null}
        <TextInput
          formik={formik}
          label="Note"
          id={'note'}
          type="text"
          inputClassName={classes.input}
          helperClassName={classes.helper}
          labelClassName={classes.label}
          formClassName={classes.formControl}
        />
        <TextInput
          formik={formik}
          required
          label="Dosage value"
          id={'dosageValue'}
          type="text"
          placeholder="0"
          inputClassName={classes.input}
          helperClassName={classes.helper}
          labelClassName={classes.label}
          formClassName={classes.formControl}
        />
        <SelectField
          formik={formik}
          label="Dosage unit"
          id={'dosageUnit'}
          options={unitOptions}
          selectRootClassName={classes.selectRoot}
          selectInputClassName={classes.selectInput}
          labelClassName={classes.label}
          formClassName={classes.formControl}
          helperClassName={classes.helper}
          type="text"
        />
        <span className={classes.frequencyContainer}>
          <TimeField
            id={'time'}
            error={''}
            label={'Frequency'}
            formik={timeFormik}
            placeholder={"Frequency"}
            inputClassName={classes.dateInput}
            helperClassName={classes.helper}
            labelClassName={classes.label}
            formClassName={classes.formControl}
            setError={() => { }}
          />
          <Button
            className={classes.addButton}
            onClick={timeFormik.submitForm}
            disabled={times.length > 4 || !timeFormik.values.time}
            variant="contained">
            Add
          </Button>
        </span>
        <p className={classes.timeContainer}>
          {times.map((time, index) => (
            <Chip size="medium" key={index} label={time} variant="outlined" onDelete={() => onDeleteTimeSlot(index)} />
          ))}
        </p>
        <DateField
          id={'startDate'}
          error={''}
          label={'Start'}
          formik={formik}
          placeholder={"DD.MM.YYYY"}
          inputClassName={classes.dateInput}
          helperClassName={classes.helper}
          labelClassName={classes.label}
          formClassName={classes.formControl}
          maxDate={formik.values.endDate}
          setError={() => { }}
        />
        <DateField
          id={'endDate'}
          error={''}
          label={'End'}
          formik={formik}
          placeholder={"DD.MM.YYYY"}
          inputClassName={classes.dateInput}
          helperClassName={classes.helper}
          labelClassName={classes.label}
          formClassName={classes.formControl}
          minDate={formik.values.startDate}
          setError={() => { }}
        />
      </div>
    </ModalWithContent>
  )
}